import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import BugReportIcon from "@mui/icons-material/BugReport";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "@context/ThemeContext";
import { useLanguageToggle } from "@hooks/useLanguageToggle";
import { FONT_HEADING, FONT_MONO } from "@/constants";
import { DRAWER_WIDTH, NAV_ITEMS } from "./constants";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { compactLabel, toggleLanguage } = useLanguageToggle();
  const { mode, toggleTheme } = useThemeMode();

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
        }}
        onClick={() => handleNav("/")}
      >
        <BugReportIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              lineHeight: 1,
              fontFamily: FONT_HEADING,
            }}
          >
            {t("app.name")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontSize: 10,
              fontFamily: FONT_MONO,
            }}
          >
            {t("app.subtitle")}
          </Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => handleNav(item.path)}
            sx={{ mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon
                sx={{
                  color:
                    location.pathname === item.path
                      ? "primary.main"
                      : "text.secondary",
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={t(item.i18nKey)}
              slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
            />
          </ListItemButton>
        ))}
      </List>

      <Divider />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={toggleTheme}
          size="small"
          title={mode === "dark" ? "Light mode" : "Dark mode"}
        >
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button
          size="small"
          onClick={toggleLanguage}
          sx={{ minWidth: 40, fontWeight: 600 }}
        >
          {compactLabel}
        </Button>
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderRight: "1px solid",
            borderColor: "divider",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        flexShrink: 0,
        width: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          maxWidth: "min(100vw - 32px, 288px)",
          boxSizing: "border-box",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
